import { createTranscriptionSession } from './transcriptionSessionService';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType,
  CreateTranscriptionSessionClientInput
} from '@/types/transcriptionSessionTypes';

export interface CreateSessionAndNavigateOptions {
  templateId?: number;
  templateName?: string;
  explicitTitle?: string;
  userId: string;
  getAccessTokenSilently: GetAccessTokenSilentlyType;
  getAccessTokenWithPopup: GetAccessTokenWithPopupType;
  navigate: (path: string) => void;
  collectionId?: string; // Optional collection ID - use existing collection
  collectionName?: string; // Optional collection name - will create if doesn't exist
}

/**
 * Creates a new transcription session and navigates to the session-specific URL
 * @param options - Options for session creation and navigation
 * @returns Promise that resolves to the created session ID or undefined if failed
 */
export const createSessionAndNavigate = async (
  options: CreateSessionAndNavigateOptions
): Promise<string | undefined> => {
  const {
    templateId,
    templateName,
    explicitTitle,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    navigate,
    collectionId,
    collectionName
  } = options;

  try {
    console.log('Creating new session for template:', templateId, templateName);

    const sessionTitle =
      explicitTitle && explicitTitle.trim()
        ? explicitTitle.trim()
        : templateName
        ? `${templateName} Session - ${new Date().toLocaleString()}`
        : `New Intake Session - ${new Date().toLocaleString()}`;

    const sessionInput: CreateTranscriptionSessionClientInput = {
      title: sessionTitle,
      templateId: templateId || null,
      sessionStartTime: new Date().toISOString(),
      collectionId: collectionId || null,
      collectionName: collectionName || null
    };

    const session = await createTranscriptionSession(
      sessionInput,
      userId,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const sessionId = session.id;

    if (sessionId) {
      console.log(
        'Created session:',
        sessionId,
        collectionId ? `in collection ID '${collectionId}'` : collectionName ? `in collection '${collectionName}'` : '',
        'navigating to /transcript/' + sessionId
      );
      navigate(`/transcript/${sessionId}`);
      return sessionId;
    } else {
      console.error('Failed to create session - no ID returned');
      return undefined;
    }
  } catch (error) {
    console.error('Failed to create session:', error);
    return undefined;
  }
};

/**
 * Extracts session ID from current URL path
 * @param pathname - Current window location pathname
 * @returns Session ID if found, null otherwise
 */
export const extractSessionIdFromUrl = (pathname: string): string | null => {
  const transcriptMatch = pathname.match(/^\/transcript\/(.+)$/);
  return transcriptMatch ? transcriptMatch[1] : null;
};

/**
 * Navigation helper to navigate to a specific session
 * @param sessionId - The session ID to navigate to
 * @param navigate - React Router navigation function
 */
export const navigateToSession = (
  sessionId: string,
  navigate: (path: string) => void
): void => {
  navigate(`/transcript/${sessionId}`);
};

/**
 * Navigation helper to navigate to the Record page (dashboard)
 * @param navigate - React Router navigation function
 */
export const navigateToNewSession = (
  navigate: (path: string) => void
): void => {
  navigate('/create');
};

// Export as a service object for easier imports
export default {
  createSessionAndNavigate,
  extractSessionIdFromUrl,
  navigateToSession,
  navigateToNewSession
};
